import { uiText, useUiLanguage } from '@/i18n/interface';

import React, { useState, useRef, useEffect } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  style = {},
  onLoad
}) => {
  useUiLanguage();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!src) return;

    const img = new Image();

    // Set loading and decoding attributes for better mobile performance
    img.loading = 'eager';
    img.decoding = 'sync';

    img.onload = () => {
      setLoaded(true);
      setError(false);
    };
    img.onerror = () => {
      setError(true);
      setLoaded(false);
    };

    // Start preloading with timeout for mobile
    const timeoutId = setTimeout(() => {
      if (!loaded) {
        setError(true);
      }
    }, 5000); // 5 second timeout

    img.src = src;

    return () => {
      img.onload = null;
      img.onerror = null;
      clearTimeout(timeoutId);
    };
  }, [src, loaded]);

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    setLoaded(true);
    if (onLoad) {
      onLoad(event);
    }
  };

  if (error) {
    return (
      <div className={`bg-gray-200 dark:bg-gray-700 flex items-center justify-center ${className}`}>
        <span className="text-xs text-gray-500">{uiText("No Image")}</span>
      </div>
    );
  }

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={className}
      style={{
        opacity: loaded ? 1 : 0,
        transition: loaded ? 'opacity 0.15s ease-out' : 'none',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        willChange: 'opacity',
        imageRendering: 'crisp-edges',
        ...style
      }}
      loading="eager"
      decoding="sync"
      onLoad={handleLoad}
      onError={() => setError(true)}
    />
  );
};
